import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import Modal from 'ui/Modal';
import { Drawer, DrawerContent } from 'ui/Drawer';
import api from 'config/axiosConfig';
import EntityAvatar from 'components/Entities/EntityAvatar';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { getActorDetails } from 'util/getObjectDetails';
import { Clock } from 'lucide-react';
import { apiRoutes } from "util/apiRoutes";

/**
 * Coauthor row shape as it appears in the API response + local enrichment.
 *
 * `status` accepts `'accepted'` (canonical post 2026-04 split), `'active'`
 * (legacy value still on pre-migration rows), `'pending'` (should never
 * appear to non-authors after the cleanup migration -- we defensively filter
 * it below anyway), and `'rejected'` for completeness.
 */
interface Coauthor {
    id: number;
    coauthor_id: number;
    type: 'user' | 'team';
    entity_id: number;
    entity_type: string;
    status: 'accepted' | 'active' | 'pending' | 'rejected';
    coauthor_entity?: any;
}

interface Props {
    entityId: number;
    entityType: 'impact' | 'solution' | 'feedback' | 'comment';
    onClose: () => void;
}

const CoauthorsModal: React.FC<Props> = ({ entityId, entityType, onClose }) => {
    const { userIsMobile, currentUser } = useUser();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coauthors, setCoauthors] = useState<Coauthor[]>([]);
    const [isDrawerVisible] = useState(true);
    const [isAuthor, setIsAuthor] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // First determine if the current user is the author
                let entityData;
                try {
                    const detailUrl =
                        entityType === "comment"
                            ? apiRoutes.comments.byId(entityId)
                            : apiRoutes.posts.byId(entityId);
                    entityData = await api.get(detailUrl);
                    if (entityData.data.user_id === currentUser?.id) {
                        setIsAuthor(true);
                    }
                } catch (error) {
                    console.error(`Error fetching ${entityType}:`, error);
                }

                // Fetch coauthors
                const response = await api.get(apiRoutes.coauthors.byEntity(entityType, entityId));
                
                // Enrich the coauthors data
                const enriched = await Promise.all(response.data.coauthors.map(async (coauthor: any) => {
                    const coauthorId = coauthor.id;
                    const coauthorType = coauthor.type;
                    
                    const enrichedData = await getActorDetails(coauthorType, coauthorId);
                    
                    return {
                        ...coauthor,
                        id: coauthorId,
                        type: coauthorType,
                        coauthor_entity: enrichedData
                    };
                }));

                setCoauthors(enriched);
            } catch (error) {
                console.error('Error fetching coauthors:', error);
                setError('Failed to load collaborators.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [entityId, entityType, currentUser?.id]);

    const content = (
        <div className="flex flex-col space-y-4">
            {loading ? (
                <LoadingSkeleton variant="entity-avatar" avatarSize="h-9 w-9" count={10} margin="mx-2"/>
            ) : error ? (
                <div className="text-warning-red dark:text-warning-red-dark text-center p-4">
                    {error}
                </div>
            ) : (() => {
                // Hide lingering `pending` rows from non-authors. Post-migration
                // no such rows exist, but this defensive filter keeps stale
                // caches / replayed responses from leaking an unanswered invite
                // onto a public surface.
                const visibleCoauthors = coauthors.filter(
                    (coauthor) => isAuthor || coauthor.status !== 'pending'
                );
                if (visibleCoauthors.length === 0) {
                    return (
                        <div className="text-eoe-text-sub dark:text-eoe-dark-text-sub text-center p-4">
                            No collaborators yet
                        </div>
                    );
                }
                return (
                    <div className="space-y-2">
                        {visibleCoauthors.map((coauthor) => (
                            <div key={`${coauthor.type}-${coauthor.id}`} className="flex items-center justify-between p-2">
                                <EntityAvatar
                                    entity={coauthor.coauthor_entity}
                                    entityType={coauthor.type}
                                    entityChip={true}
                                    showCloseChip={false}
                                    isInputField={false}
                                    showLink={true}
                                    badgeElement={
                                        coauthor.status === 'pending' ?
                                        <Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" /> :
                                        undefined
                                    }
                                />
                                {isAuthor && coauthor.status === 'pending' && (
                                    <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">Pending</span>
                                )}
                            </div>
                        ))}
                    </div>
                );
            })()}
        </div>
    );

    return (
        <>
            {userIsMobile ? (
                isDrawerVisible ? (
                    <Drawer open={true} onOpenChange={onClose}>
                        <DrawerContent title="Collaborators">
                            {content}
                        </DrawerContent>
                    </Drawer>
                ) : null
            ) : (
                <Modal 
                    isOpen={true} 
                    onClose={onClose} 
                    title="Collaborators"
                    footer={(
                        <div className="flex justify-end py-2">
                            <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
                        </div>
                    )}
                    stickyFooter
                >
                    {content}
                </Modal>
            )}
        </>
    );
};

export default CoauthorsModal;
